import React, { useState, useEffect, useCallback } from 'react';
import { childrenStatisticsActions } from './actions';
import DonutWithText from './components/diagram';
import { copyTableToClipboard, downloadTableXls } from '../../../../../utils/exportTableExcell';

const Month = [
  'Вересень', 'Жовтень', 'Листопад', 'Грудень', 'Січень', 'Лютий', 
  'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень'
];

const StatisticaChildrens = (props) => {
  const curDate = new Date();
  const curMonth = curDate.getMonth();
  const curMonthArr = curMonth < 8 ? curMonth + 5 : curMonth - 7;
  const currentStudyYear = curMonth < 5 ? curDate.getFullYear() - 1 : curDate.getFullYear();

  const [studyYear, setStudyYear] = useState(currentStudyYear);
  const [thisStudyYear] = useState(currentStudyYear);
  const [kyivReg, setKyivReg] = useState('all');
  const [kyivMr, setKyivMr] = useState('all');
  const [stateQueueLengthDistr, setStateQueueLengthDistr] = useState({});
  const [stateQueueLengthAll, setStateQueueLengthAll] = useState({});
  const [benefitsAll, setBenefitsAll] = useState([]);
  const [benCur, setBenCur] = useState('wo');
  const [invSts, setInvSts] = useState('all');
  const [inKg, setInKg] = useState('all');
  const [reqCrt, setReqCrt] = useState('all');
  const [years, setYears] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentStudyYear);
  const [selectedDist, setSelectedDist] = useState('');
  const [specializations, setSpecializations] = useState([]);
  const [selectedSpec, setSelectedSpec] = useState('all');
  const [typeSit, setTypeSit] = useState('all');
  const [availableMonths, setAvailableMonths] = useState(Month.slice(0, curMonthArr));
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [showComponent, setShowComponent] = useState(() => <div></div>);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Функция для получения заголовка таблицы
  const getChildrenTableTitle = useCallback(() => {
    const monthName = Month[selectedMonth] || Month[0];
    const currentDateStr = new Date().toLocaleDateString('uk-UA');
    return `Діти в садочках ${selectedYear}/${parseInt(selectedYear) + 1} навчальний рік, станом на ${monthName} ${currentDateStr}`;
  }, [selectedYear, selectedMonth]);

  // Обработчик копирования
  const handleCopyToClipboard = useCallback(() => {
    const title = getChildrenTableTitle();
    copyTableToClipboard('childrenStatisticsTable', title, {
      removeSelectors: ['button', '.action-btn', 'input', 'img', '.expand-toggle-btn'],
      stripedEvenColor: '#ffffff',
      stripedOddColor: '#f8f9fa',
      theadColor: '#cfe2ff',
    });
  }, [getChildrenTableTitle]);

  // Обработчик скачивания
  const handleDownloadXls = useCallback(() => {
    const title = getChildrenTableTitle();
    const filename = `children_statistics_${selectedYear}_${new Date().toISOString().split('T')[0]}`;
    downloadTableXls('childrenStatisticsTable', title, filename, {
      locale: 'uk-UA',
      removeSelectors: ['button', '.action-btn', 'input', 'img', '.expand-toggle-btn'],
      stripedEvenColor: '#ffffff',
      stripedOddColor: '#f8f9fa',
      theadColor: '#cfe2ff',
    });
  }, [getChildrenTableTitle, selectedYear]);

  // Получение периодов и районов при монтировании компонента
  useEffect(() => {
    getPeriodYearsAndDistricts();
  }, []);

  const getPeriodYearsAndDistricts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await childrenStatisticsActions.getKindergartenGroupsYears();
      
      if (response.data?.cursor?.length > 0) {
        const processedYears = response.data.cursor.map(yearData => {
          const distr = new Set();
          let solPresent = false;
          
          yearData.dist.forEach(item => {
            if (!item.match(/Со/)) {
              distr.add(item);
            } else {
              solPresent = true;
            }
          });

          if (solPresent) {
            distr.add('Солом\'янський');
          }

          return {
            ...yearData,
            dist: Array.from(distr).sort()
          };
        });

        const curYear = curMonth > 8 ? curDate.getFullYear() : curDate.getFullYear() - 1;
        const index = processedYears.findIndex(el => el._id === curYear);
        const selectedIndex = index >= 0 ? index : 0;
        const selectedYearData = processedYears[selectedIndex];

        setYears(processedYears);
        setDistricts(selectedYearData.dist);
        setSpecializations(selectedYearData.specializations?.sort() || []);
        setSelectedYear(selectedYearData._id);
        setSelectedDist(selectedYearData.dist[0] || '');
      } else {
        setShowComponent(() => <div className="alert alert-info text-center">Немає даних для відображення</div>);
      }
    } catch (err) {
      setError(`Помилка при отриманні даних: ${err}`);
    } finally {
      setLoading(false);
    }
  }, [curMonth, curDate]);

  const handleGetInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const periodMonth = +selectedMonth > 3 ? +selectedMonth - 4 : +selectedMonth + 8;
      const yearTo = periodMonth < 8 ? selectedYear + 1 : selectedYear;

      const data = {
        period_start_year: selectedYear,
        date_from: new Date(yearTo, periodMonth, 1).toISOString(),
        date_to: new Date(yearTo, periodMonth + 1, 0).toISOString(),
        status_mr: kyivReg !== 'all' ? 'true' : 'all',
        spec: selectedSpec,
        type_place: typeSit
      };

      setShowComponent(() => <div className="alert alert-info text-center">Рахуємо, зачекайте ...</div>);

      await getQueueDistrict(data);
    } catch (err) {
      setError(`Помилка при отриманні інформації: ${err}`);
      setShowComponent(() => <div className="alert alert-warning text-center">Вибачте, за вказаними параметрами інформації не має, спробуйте ще.</div>);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, kyivReg, selectedSpec, typeSit]);

  const getQueueDistrict = useCallback(async (data) => {
    try {
      const response = await childrenStatisticsActions.getChildrenGeneralStatistics(data);
      
      if (response.data?.length > 0) {
        let queueLengthDistr = {};

        response.data.forEach(element => {
          if (!queueLengthDistr.hasOwnProperty(element._id.district)) {
            queueLengthDistr[element._id.district] = {};
          }
          queueLengthDistr[element._id.district][parseInt(element._id.age)] = element.sum;
        });

        // Заполняем отсутствующие возрасты нулями
        Object.keys(queueLengthDistr).forEach(element => {
          for (let i = 1971; i < 1978; i++) {
            if (!queueLengthDistr[element].hasOwnProperty(i)) {
              queueLengthDistr[element][i] = 0;
            }
          }
        });

        // Обрабатываем Соломянский район
        let solDistr = {};
        for (let i = 1971; i < 1978; i++) {
          solDistr[i] = 0;
        }

        Object.keys(queueLengthDistr).forEach(element => {
          if (element.match(/Со/)) {
            for (let i = 1971; i < 1978; i++) {
              solDistr[i] += queueLengthDistr[element][i];
            }
            delete queueLengthDistr[element];
          }
        });

        queueLengthDistr['Солом\'янський'] = solDistr;

        // Считаем общую статистику по Киеву
        let queueLengthAll = {};
        Object.keys(queueLengthDistr).forEach(distr => {
          Object.keys(queueLengthDistr[distr]).forEach(age => {
            if (!age || +age < 1970 || +age > 1978) return;
            if (!queueLengthAll.hasOwnProperty(+age)) queueLengthAll[+age] = 0;
            queueLengthAll[+age] += queueLengthDistr[distr][+age];
          });
        });

        setStateQueueLengthDistr(queueLengthDistr);
        setStateQueueLengthAll(queueLengthAll);
        setShowComponent(() => tableInfo(queueLengthDistr, queueLengthAll));
      } else {
        setShowComponent(() => <div className="alert alert-warning text-center">Вибачте, за вказаними параметрами інформації не має, спробуйте ще.</div>);
      }
    } catch (err) {
      throw new Error(`Помилка при отриманні статистики: ${err}`);
    }
  }, []);

  const handleSetYear = useCallback((e) => {
    const newSelectedYear = +e.target.value;
    const yearData = years.find(year => year._id === newSelectedYear);
    
    if (!yearData) return;

    let countSol = 0;
    let districtsList = [];

    yearData.dist.forEach(item => {
      if (item.match(/Со/) && countSol === 0) {
        countSol += 1;
        districtsList.push('Солом\'янський');
      } else {
        districtsList.push(item);
      }
    });

    const currentYear = new Date().getMonth() > 8 ? new Date().getFullYear() : new Date().getFullYear() - 1;
    const monthArr = newSelectedYear < currentYear ? Month : newSelectedYear > currentYear ? [] : Month.slice(0, curMonthArr);

    setDistricts(districtsList.sort());
    setSelectedYear(newSelectedYear);
    setSpecializations(yearData.specializations?.sort() || []);
    setAvailableMonths(monthArr);
    setSelectedDist(districtsList[0] || '');
  }, [years, curMonthArr]);

  const tableInfo = useCallback((queueDistr, queueAll) => {
    const tableDistr = Object.keys(queueDistr).map((item, i) => {
      let allChildCount = 0;
      for (let index = 1970; index < 1979; index++) {
        if (queueDistr[item].hasOwnProperty(index)) allChildCount += queueDistr[item][index];
      }

      return (
        <tr key={i} className={i % 2 === 0 ? 'table-light' : ''}>
          <td className="fw-bold">{item}</td>
          <td>{queueDistr[item]?.[1970] || '0'}</td>
          <td>{queueDistr[item]?.[1971] || '0'}</td>
          <td>{queueDistr[item]?.[1972] || '0'}</td>
          <td>{queueDistr[item]?.[1973] || '0'}</td>
          <td>{queueDistr[item]?.[1974] || '0'}</td>
          <td>{queueDistr[item]?.[1975] || '0'}</td>
          <td>{queueDistr[item]?.[1976] || '0'}</td>
          <td>{queueDistr[item]?.[1977] || '0'}</td>
          <td>{queueDistr[item]?.[1978] || '0'}</td>
          <td className="fw-semibold">{allChildCount}</td>
          <td className="fw-semibold">
            {(queueDistr[item]?.[1973] || 0) + (queueDistr[item]?.[1974] || 0) + (queueDistr[item]?.[1975] || 0)}
          </td>
        </tr>
      );
    });

    const getAllChildCountKyiv = () => {
      let allChildCountKyiv = 0;
      Object.keys(queueAll).forEach(item => {
        if (item > 1969 && item < 1979) allChildCountKyiv += queueAll[item];
      });
      return allChildCountKyiv;
    };

    const tableAll = (
      <tr className="table-dark fw-bold">
        <td>По Києву:</td>
        <td>{queueAll[1970] > 0 ? queueAll[1970] : 0}</td>
        <td>{queueAll[1971] > 0 ? queueAll[1971] : 0}</td>
        <td>{queueAll[1972] > 0 ? queueAll[1972] : 0}</td>
        <td>{queueAll[1973] > 0 ? queueAll[1973] : 0}</td>
        <td>{queueAll[1974] > 0 ? queueAll[1974] : 0}</td>
        <td>{queueAll[1975] > 0 ? queueAll[1975] : 0}</td>
        <td>{queueAll[1976] > 0 ? queueAll[1976] : 0}</td>
        <td>{queueAll[1977] > 0 ? queueAll[1977] : 0}</td>
        <td>{queueAll[1978] > 0 ? queueAll[1978] : 0}</td>
        <td>{getAllChildCountKyiv()}</td>
        <td>{(queueAll[1973] || 0) + (queueAll[1974] || 0) + (queueAll[1975] || 0)}</td>
      </tr>
    );

    const monthName = Month[selectedMonth] || Month[0];
    const currentDateStr = new Date().toLocaleDateString('uk-UA');

    return (
      <div>
        <div className="row mb-3">
          <div className="col-md-8">
            <p className="h4 text-center mb-4">
              Діти в садочках {selectedYear}/{parseInt(selectedYear) + 1} навчальний рік<br />
              <small className="text-muted">Станом на {monthName} {currentDateStr}</small>
            </p>
          </div>
          <div className="col-md-4 text-end">
            <div className="btn-group btn-group-sm">
              <button 
                className="btn btn-outline-secondary"
                onClick={handleCopyToClipboard}
                title="Скопіювати таблицю для Excel"
              >
                <i className="bi bi-clipboard-data"></i> Копіювати
              </button>
              <button 
                className="btn btn-outline-success"
                onClick={handleDownloadXls}
                title="Завантажити як Excel файл"
              >
                <i className="bi bi-file-earmark-excel"></i> .xls
              </button>
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover" id="childrenStatisticsTable">
            <thead className="table-primary">
              <tr>
                <th scope="col">Район</th>
                <th scope="col">0-1 року</th>
                <th scope="col">1-2 роки</th>
                <th scope="col">2-3 роки</th>
                <th scope="col">3-4 роки</th>
                <th scope="col">4-5 роки</th>
                <th scope="col">5-6 років</th>
                <th scope="col">6-7 років</th>
                <th scope="col">7-8 років</th>
                <th scope="col">8-9 років</th>
                <th scope="col">Разом</th>
                <th scope="col">Разом 3-6</th>
              </tr>
            </thead>
            <tbody>
              {tableDistr}
              {tableAll}
            </tbody>
          </table>
        </div>

        <div className="col-md-12 mt-4">
          <div id="chart_div">
            <DonutWithText 
              dataset={[1, 2, 3, 4, 5, 6, 7, 8].map(a => 
                !isNaN(+queueAll[1970 + a]) ? +queueAll[1970 + a] : 0
              )}
              title="Розподіл дітей за віком"
            />
          </div>
        </div>
      </div>
    );
  }, [selectedYear, selectedMonth, handleCopyToClipboard, handleDownloadXls]);

  if (loading && !showComponent) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
        <span className="ms-2">Завантаження...</span>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger text-center">Помилка: {error}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-6 col-lg-2">
              <label htmlFor="studyYear" className="form-label">Навчальний рік:</label>
              <select 
                id="studyYear"
                className="form-select" 
                value={selectedYear} 
                onChange={handleSetYear}
                disabled={years.length === 0}
              >
                {years.map((elem, index) => (
                  <option key={index} value={elem._id}>
                    {elem._id}/{elem._id + 1}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label htmlFor="kyivReg" className="form-label">Реєстрація в Києві</label>
              <select 
                id="kyivReg"
                className="form-select" 
                value={kyivReg} 
                onChange={(e) => setKyivReg(e.target.value)}
              >
                <option value='all'>Всі</option>
                <option value='kyiv_reg'>З підтвердженою реєстрацією</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label htmlFor="typeSit" className="form-label">Тип місця</label>
              <select 
                id="typeSit"
                className="form-select" 
                value={typeSit} 
                onChange={(e) => setTypeSit(e.target.value)}
              >
                <option value='all'>Всі</option>
                <option value='standart'>Повного дня</option>
                <option value='without-bed'>Короткотривалого перебування</option>
                <option value='inclusive'>Інклюзивне</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label htmlFor="selectedSpec" className="form-label">Спеціалізація</label>
              <select 
                id="selectedSpec"
                className="form-select" 
                value={selectedSpec} 
                onChange={(e) => setSelectedSpec(e.target.value)}
              >
                <option value='all'>будь-яка</option>
                {specializations.map((spec, index) => (
                  <option key={index} value={spec}>{spec}</option>
                ))}
              </select>
            </div>

            <div 
              className={`col-md-6 col-lg-3 ${availableMonths.length > 0 ? '' : 'opacity-50'}`}
            >
              <label htmlFor="selectedMonth" className="form-label">Станом на:</label>
              <select 
                id="selectedMonth"
                className="form-select" 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={availableMonths.length === 0}
              >
                {availableMonths.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>
          </div>

          <div className='row'>
            <div className="col-md-12 text-center">
              <button 
                className="btn btn-primary px-4" 
                onClick={handleGetInfo} 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Завантаження...
                  </>
                ) : 'Показати'}
              </button>
            </div>
          </div>

          <div className="mt-4">
            {showComponent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticaChildrens;